import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, money, statusBadge, categoryColor, fileSrc } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Sparkles, Trash2, Loader2, FileText, Image as ImageIcon, Eye, EyeOff, HardHat, ChevronRight, Download, Send, CheckCheck } from "lucide-react";
import { WorkAssignments } from "../components/WorkAssignments";

const manage = (r) => ["admin", "manager", "estimator", "pm"].includes(r);
const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export default function Estimates() {
  const { t } = useLang();
  const { user } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [techs, setTechs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ticket_id: "", title: "", line_items: [], subcontracts: [], work_assignments: [], notes: "", tax_rate: 0 });
  const [aiLoading, setAiLoading] = useState(false);
  const [visionLoading, setVisionLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [observations, setObservations] = useState("");
  const [workDescription, setWorkDescription] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [sendDialog, setSendDialog] = useState(null); // { est, to, busy }
  const [rejectDialog, setRejectDialog] = useState(null); // { est, reason }
  const [showCompleted, setShowCompleted] = useState(false);

  const load = () => api.get(`/estimates?view=${showCompleted ? "all" : "active"}`).then(setEstimates);
  useEffect(() => {
    load();
    if (manage(user.role)) {
      api.get("/tickets").then(setTickets).catch(() => {});
      api.get("/subcontractors").then(setSubcontractors).catch(() => {});
      api.get("/technicians").then(setTechs).catch(() => {});
    }
  }, [showCompleted]); // eslint-disable-line

  // Pre-fill AI description + photos from the linked ticket
  const prefillFromTicket = (tk) => {
    setWorkDescription(tk?.description || "");
    const photoFiles = (tk?.photos || []).map((url) => ({
      id: String(url).split("?")[0].split("/").pop(),
      url, content_type: "image/jpeg", filename: "photo",
    }));
    setFiles(photoFiles);
  };

  // Deep link from a ticket: /estimates?ticket=<id> opens the estimate flow
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    const tid = searchParams.get("ticket");
    if (!tid || deepLinkHandled.current) return;
    if (tickets.length === 0) return;
    deepLinkHandled.current = true;
    const existing = estimates.find((e) => e.ticket_id === tid);
    if (existing) {
      openEdit(existing);
    } else {
      const tk = tickets.find((x) => x.id === tid);
      setEditing(null);
      setForm({ ticket_id: tid, title: tk?.title || "", line_items: [], subcontracts: [], work_assignments: [], notes: "", tax_rate: 0 });
      setObservations("");
      prefillFromTicket(tk);
      setOpen(true);
    }
    setSearchParams({}, { replace: true });
  }, [estimates, tickets]); // eslint-disable-line

  const subtotal = (form.line_items || []).reduce((s, li) => s + (li.quantity || 0) * (li.unit_price || 0), 0);
  const tax = subtotal * (form.tax_rate || 0) / 100;
  const total = subtotal + tax;

  const openNew = () => {
    setEditing(null);
    setForm({ ticket_id: "", title: "", line_items: [], subcontracts: [], work_assignments: [], notes: "", tax_rate: 0 });
    setFiles([]);
    setObservations("");
    setWorkDescription("");
    setOpen(true);
  };
  const openEdit = (est) => {
    setEditing(est);
    setForm({
      ...est, line_items: est.line_items || [], subcontracts: est.subcontracts || [],
      work_assignments: est.work_assignments?.length ? est.work_assignments
        : (est.subcontracts || []).map((sc) => ({ id: sc.id, type: "subcontractor", ref_id: sc.subcontractor_id || "", name: sc.subcontractor_name || "", trades: [], value: sc.value || 0 })),
    });
    setFiles([]);
    setObservations(est.observations || "");
    setWorkDescription("");
    setOpen(true);
  };

  const uploadFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const res = await api.upload(f);
        setFiles((prev) => [...prev, res]);
      }
    } catch {
      toast.error(t("error"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const generateFromImages = async () => {
    if (files.length === 0) return toast.error(t("uploadHint"));
    const ticket = tickets.find((x) => x.id === form.ticket_id);
    setVisionLoading(true);
    try {
      const res = await api.post("/estimates/ai-generate-vision", {
        description: workDescription.trim() || form.notes || (ticket ? `${ticket.title}. ${ticket.description}` : form.title),
        category: ticket?.category || "general",
        title: ticket?.title || form.title,
        file_ids: files.map((f) => f.id),
      });
      setForm((f) => ({ ...f, line_items: res.line_items, notes: f.notes || res.notes }));
      setObservations(res.observations || "");
      toast.success("AI " + t("saved"));
    } catch (e) {
      toast.error(t("error"));
    } finally {
      setVisionLoading(false);
    }
  };

  const updateItem = (i, key, val) => {
    const items = [...form.line_items];
    items[i] = { ...items[i], [key]: key === "description" || key === "type" ? val : parseFloat(val) || 0 };
    setForm({ ...form, line_items: items });
  };
  const addItem = () => setForm({ ...form, line_items: [...form.line_items, { description: "", type: "labor", quantity: 1, unit_price: 0 }] });
  const removeItem = (i) => setForm({ ...form, line_items: form.line_items.filter((_, j) => j !== i) });

  const generateAI = async () => {
    const ticket = tickets.find((x) => x.id === form.ticket_id);
    const parts = [];
    if (ticket) parts.push(`${ticket.title}. ${ticket.description || ""}`);
    if (workDescription.trim()) parts.push(workDescription.trim());
    if (!parts.length && form.title) parts.push(form.title);
    const desc = parts.join(" ").trim();
    if (!desc) return toast.error(t("aiNeedDescription"));
    setAiLoading(true);
    try {
      const res = await api.post("/estimates/ai-generate", {
        description: desc,
        category: ticket?.category || "general",
        title: ticket?.title || form.title,
      });
      setForm((f) => ({
        ...f,
        line_items: res.line_items,
        notes: f.notes || res.notes,
        title: f.title || ticket?.title || "",
      }));
      toast.success("AI " + t("saved"));
    } catch (e) {
      toast.error(t("error"));
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    if (!editing && !form.ticket_id) return toast.error(t("selectTicket"));
    try {
      if (editing) {
        await api.put(`/estimates/${editing.id}`, {
          title: form.title, line_items: form.line_items,
          work_assignments: form.work_assignments || [], notes: form.notes, tax_rate: form.tax_rate,
        });
      } else {
        await api.post("/estimates", form);
      }
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(t("error"));
    }
  };

  const changeStatus = async (est, status, reason) => {
    try {
      await api.post(`/estimates/${est.id}/status`, { status, reason });
      toast.success(t("saved"));
      load();
    } catch (e) {
      toast.error(t("error"));
    }
  };

  const submitReject = async () => {
    const { est, reason } = rejectDialog;
    if (!reason?.trim()) return toast.error(t("rejectReasonRequired"));
    await changeStatus(est, "rejected", reason.trim());
    setRejectDialog(null);
  };

  const sendToPurchasing = async (est) => {
    try {
      await api.post(`/estimates/${est.id}/send-to-purchasing`);
      toast.success(t("sendToPurchasing"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    }
  };

  const downloadPdf = async (est) => {
    try {
      const blob = await api.getBlob(`/estimates/${est.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${est.number || "estimate"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("error")); }
  };

  const sendPdf = async () => {
    if (!sendDialog?.to?.trim()) return toast.error(t("recipientEmail"));
    setSendDialog((s) => ({ ...s, busy: true }));
    try {
      await api.post(`/estimates/${sendDialog.est.id}/send-pdf`, { to: sendDialog.to.trim() });
      toast.success(t("emailSent"));
      setSendDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
      setSendDialog((s) => ({ ...s, busy: false }));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("estimates")}</h1>
        <div className="flex items-center gap-2">
          {manage(user.role) && (
            <button data-testid="toggle-completed-btn" onClick={() => setShowCompleted((v) => !v)} className={`inline-flex items-center gap-1.5 rounded-lg text-sm font-medium px-3 py-2.5 border transition-colors ${showCompleted ? "bg-slate-100 border-slate-300 text-slate-700" : "border-slate-300 text-slate-500 hover:bg-slate-50"}`}>
              {showCompleted ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showCompleted ? t("hideCompleted") : t("showCompleted")}
            </button>
          )}
          {manage(user.role) && (
            <button data-testid="new-estimate-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2.5 hover:bg-slate-800 transition-colors">
              <Plus className="w-4 h-4" /> {t("newEstimate")}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3">{t("number")}</th>
                <th className="text-left px-4 py-3">{t("title")}</th>
                <th className="text-left px-4 py-3">{t("propertyCol")}</th>
                <th className="text-left px-4 py-3">{t("serviceType")}</th>
                <th className="text-center px-4 py-3">{t("lineItems")}</th>
                <th className="text-right px-4 py-3">{t("total")}</th>
                <th className="text-left px-4 py-3">{t("status")}</th>
                <th className="text-right px-4 py-3">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {estimates.map((est) => {
                const isClient = user.role === "client";
                const isOpen = expandedId === est.id && !isClient;
                const subCount = (est.subcontracts || []).length;
                return (
                  <React.Fragment key={est.id}>
                    <tr data-testid={`estimate-row-${est.id}`} onClick={() => { if (!isClient) setExpandedId(isOpen ? null : est.id); }} className={`border-b border-slate-100 transition-colors ${isClient ? "" : "hover:bg-slate-50 cursor-pointer"} ${isOpen ? "bg-slate-50" : ""}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        <div className="flex items-center gap-1.5">
                          {!isClient && <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />}
                          {est.number}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{est.title}</div>
                        <div className="text-xs text-slate-400">{est.unit_name}</div>
                        {user.role === "client" && est.status === "sent" && (
                          <span className="inline-block mt-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 rounded px-1.5 py-0.5">{t("awaitingApproval")}</span>
                        )}
                        {est.status === "rejected" && est.rejection_reason && (
                          <div data-testid={`reject-reason-${est.id}`} className="mt-1 text-[11px] text-red-600 bg-red-50 rounded px-2 py-1 max-w-xs">
                            <span className="font-semibold">{t("rejectedReason")}:</span> {est.rejection_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {est.property_name || est.unit_name ? (
                          <div>
                            <div className="text-xs font-medium text-slate-700">{est.property_name || "—"}</div>
                            {est.unit_name && <div className="text-[11px] text-slate-400">{est.unit_name}</div>}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded ${categoryColor(est.category)}`}>{t(est.category || "general")}</span></td>
                      <td className="px-4 py-3 text-center text-slate-600">
                        {est.line_items_count ?? (est.line_items || []).length}
                        {subCount > 0 && <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-600"><HardHat className="w-3 h-3" />{subCount}</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900">{money(est.total)}</td>
                      <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(est.status)}`}>{t(est.status)}</span></td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end flex-wrap gap-1.5">
                          {manage(user.role) && (
                            <button data-testid={`view-estimate-${est.id}`} onClick={() => openEdit(est)} className="text-xs font-medium border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {t("edit")}</button>
                          )}
                          <button data-testid={`pdf-estimate-${est.id}`} onClick={() => downloadPdf(est)} className="text-xs font-medium border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> {t("downloadPdf")}</button>
                          {manage(user.role) && (
                            <button data-testid={`send-pdf-${est.id}`} onClick={() => setSendDialog({ est, to: "", busy: false })} className="text-xs font-medium border border-blue-300 text-blue-700 rounded-lg px-2.5 py-1.5 hover:bg-blue-50 inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> {t("sendPdf")}</button>
                          )}
                          {manage(user.role) && est.status === "draft" && (
                            <button data-testid={`send-estimate-${est.id}`} onClick={() => changeStatus(est, "sent")} className="text-xs font-medium bg-indigo-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-indigo-700">{t("sendToClient")}</button>
                          )}
                          {manage(user.role) && est.status === "rejected" && (
                            <button data-testid={`resend-estimate-${est.id}`} onClick={() => changeStatus(est, "sent")} className="text-xs font-medium bg-indigo-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-indigo-700 inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5" />{t("resendToClient")}</button>
                          )}
                          {(user.role === "client" || ["admin", "manager"].includes(user.role)) && est.status === "sent" && (
                            <>
                              <button data-testid={`approve-estimate-${est.id}`} onClick={() => changeStatus(est, "approved")} className="text-xs font-medium bg-emerald-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-emerald-700">{t("approve")}</button>
                              <button data-testid={`reject-estimate-${est.id}`} onClick={() => setRejectDialog({ est, reason: "" })} className="text-xs font-medium bg-white border border-red-300 text-red-600 rounded-lg px-2.5 py-1.5 hover:bg-red-50">{t("reject")}</button>
                            </>
                          )}
                          {["admin", "manager"].includes(user.role) && est.status === "approved" && (
                            <button data-testid={`execute-estimate-${est.id}`} onClick={() => { if (window.confirm(t("confirmExecuted"))) changeStatus(est, "executed"); }} className="text-xs font-medium bg-violet-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-violet-700 inline-flex items-center gap-1.5"><CheckCheck className="w-3.5 h-3.5" />{t("markExecuted")}</button>
                          )}
                          {manage(user.role) && est.status === "approved" && (est.line_items || []).some((li) => li.type === "material") && (
                            <button data-testid={`send-purchasing-${est.id}`} onClick={() => sendToPurchasing(est)} className="text-xs font-medium bg-fuchsia-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-fuchsia-700">{t("sendToPurchasing")}</button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`estimate-expanded-${est.id}`} className="border-b border-slate-200 bg-slate-50/70">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">{t("lineItems")}</div>
                          <table className="w-full text-sm mb-3">
                            <tbody>
                              {(est.line_items || []).map((li, i) => (
                                <tr key={i} className="border-b border-slate-100 last:border-0">
                                  <td className="py-1.5 text-slate-700">{li.description}</td>
                                  <td className="py-1.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${li.type === "material" ? "bg-cyan-50 text-cyan-600" : "bg-slate-100 text-slate-600"}`}>{t(li.type)}</span></td>
                                  <td className="py-1.5 text-right text-slate-500 tabular-nums">{li.quantity} × {money(li.unit_price)}</td>
                                  <td className="py-1.5 text-right font-medium tabular-nums w-24">{money((li.quantity || 0) * (li.unit_price || 0))}</td>
                                </tr>
                              ))}
                              {(est.line_items || []).length === 0 && <tr><td className="py-2 text-slate-400">{t("noData")}</td></tr>}
                            </tbody>
                          </table>
                          {subCount > 0 && (
                            <>
                              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1.5"><HardHat className="w-3.5 h-3.5 text-fuchsia-600" />{t("subcontracts")} <span className="font-normal text-slate-400 normal-case">· {t("internalCost")}</span></div>
                              <div className="space-y-1">
                                {(est.subcontracts || []).map((sc) => (
                                  <div key={sc.id} className="flex items-center justify-between text-sm bg-white border border-slate-100 rounded-lg px-3 py-1.5">
                                    <span className="text-slate-700">{sc.subcontractor_name} <span className="text-slate-400">— {sc.service}</span></span>
                                    <span className="font-medium tabular-nums text-fuchsia-700">{money(sc.value)}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {estimates.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">{t("noData")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {rejectDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setRejectDialog(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{t("rejectEstimate")}</h2>
              <button onClick={() => setRejectDialog(null)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-sm font-medium text-slate-900">{rejectDialog.est.title}</div>
                <div className="text-xs text-slate-400">{rejectDialog.est.number} · {money(rejectDialog.est.total)}</div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("rejectReason")}</label>
                <textarea data-testid="reject-reason-input" rows={4} value={rejectDialog.reason} onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })} placeholder={t("rejectReasonHint")} className={inputCls} autoFocus />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setRejectDialog(null)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="reject-confirm" onClick={submitReject} className="rounded-lg px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700">{t("confirmReject")}</button>
            </div>
          </div>
        </div>
      )}

      {sendDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => !sendDialog.busy && setSendDialog(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{t("sendEstimatePdf")}</h2>
              <button onClick={() => setSendDialog(null)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-sm font-medium text-slate-900">{sendDialog.est.title}</div>
                <div className="text-xs text-slate-400">{sendDialog.est.number} · {money(sendDialog.est.total)}</div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("recipientEmail")}</label>
                <input data-testid="send-pdf-email" type="email" value={sendDialog.to} onChange={(e) => setSendDialog({ ...sendDialog, to: e.target.value })} placeholder="client@email.com" className={inputCls} autoFocus />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setSendDialog(null)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="send-pdf-confirm" onClick={sendPdf} disabled={sendDialog.busy} className="rounded-lg px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-60">
                {sendDialog.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{sendDialog.busy ? t("sending") : t("sendPdf")}
              </button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-slate-900">{editing ? t("estimates") : t("newEstimate")}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              {!editing && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("estimateFor")}</label>
                  <select data-testid="estimate-ticket-select" value={form.ticket_id} onChange={(e) => { const tk = tickets.find((x) => x.id === e.target.value); setForm({ ...form, ticket_id: e.target.value, title: tk?.title || "" }); prefillFromTicket(tk); }} className={inputCls}>
                    <option value="">{t("selectTicket")}</option>
                    {tickets.map((tk) => <option key={tk.id} value={tk.id}>{tk.number} — {tk.title}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("title")}</label>
                <input data-testid="estimate-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2.5">
                <p className="text-xs text-blue-800">{t("aiHint")}</p>
                <textarea data-testid="ai-describe-input" rows={2} value={workDescription} onChange={(e) => setWorkDescription(e.target.value)} placeholder={t("aiDescribePlaceholder")} className={inputCls} />
                <div className="flex justify-end">
                  <button data-testid="ai-generate-btn" onClick={generateAI} disabled={aiLoading} className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white text-sm font-medium px-3.5 py-2 hover:bg-blue-700 transition-colors disabled:opacity-60">
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {aiLoading ? t("generating") : t("generateAI")}
                  </button>
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg p-3 space-y-3">
                <p className="text-xs text-slate-600">{t("uploadHint")}</p>
                <div className="flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <div key={f.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center">
                      {f.content_type?.startsWith("image/") ? (
                        <img src={fileSrc(f.url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center text-slate-500 px-1 text-center"><FileText className="w-6 h-6" /><span className="text-[9px] truncate w-16">{f.filename}</span></div>
                      )}
                      <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"><X className="w-3 h-3 text-white" /></button>
                    </div>
                  ))}
                  <label className="w-20 h-20 rounded-lg border border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:bg-slate-50 cursor-pointer text-center">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
                    <span className="text-[9px] mt-1 px-1">{t("dropFiles")}</span>
                    <input data-testid="estimate-file-input" type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={uploadFiles} />
                  </label>
                </div>
                <button data-testid="ai-vision-btn" onClick={generateFromImages} disabled={visionLoading || files.length === 0} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-3.5 py-2 hover:bg-slate-800 transition-colors disabled:opacity-50">
                  {visionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  {visionLoading ? t("analyzing") : t("generateFromImages")}
                </button>
                {observations && (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 text-xs text-amber-900">
                    <span className="font-semibold">{t("aiObservations")}: </span>{observations}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("lineItems")}</label>
                  <button data-testid="add-line-btn" onClick={addItem} className="text-xs font-medium text-blue-600 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> {t("addLine")}</button>
                </div>
                <div className="space-y-2">
                  {form.line_items.map((li, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input value={li.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder={t("description")} className={`${inputCls} col-span-5`} />
                      <select value={li.type} onChange={(e) => updateItem(i, "type", e.target.value)} className={`${inputCls} col-span-2`}>
                        <option value="labor">{t("labor")}</option>
                        <option value="material">{t("material")}</option>
                      </select>
                      <input type="number" value={li.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} placeholder={t("qty")} className={`${inputCls} col-span-1`} />
                      <input type="number" value={li.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} placeholder={t("unitPrice")} className={`${inputCls} col-span-2`} />
                      <div className="col-span-1 text-right text-sm font-medium text-slate-700">{money((li.quantity || 0) * (li.unit_price || 0))}</div>
                      <button onClick={() => removeItem(i)} className="col-span-1 flex justify-center text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {form.line_items.length === 0 && <p className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">{t("noData")}</p>}
                </div>
              </div>

              <WorkAssignments value={form.work_assignments || []} onChange={(wa) => setForm({ ...form, work_assignments: wa })}
                techs={techs} subs={subcontractors} onSubsReload={() => api.get("/subcontractors").then(setSubcontractors).catch(() => {})} />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("taxRate")}</label>
                  <input data-testid="estimate-tax" type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })} className={inputCls} />
                </div>
                <div className="flex flex-col justify-end text-sm space-y-1">
                  <div className="flex justify-between text-slate-500"><span>{t("subtotal")}</span><span className="font-medium text-slate-700">{money(subtotal)}</span></div>
                  <div className="flex justify-between text-slate-500"><span>{t("tax")}</span><span className="font-medium text-slate-700">{money(tax)}</span></div>
                  <div className="flex justify-between text-base font-bold text-slate-900 pt-1 border-t border-slate-200"><span>{t("total")}</span><span>{money(total)}</span></div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("notes")}</label>
                <textarea data-testid="estimate-notes" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="estimate-save" onClick={save} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("saveEstimate")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
